import { lazy, type ComponentType } from 'react';

const reloadedChunks = new Set<string>();

const getReloadKey = (name: string) => `lazy-retry-reloaded:${name}`;

const hasReloadedChunk = (name: string) => {
  const reloadKey = getReloadKey(name);

  if (reloadedChunks.has(reloadKey)) {
    return true;
  }

  try {
    return window.sessionStorage.getItem(reloadKey) === 'true';
  } catch {
    return false;
  }
};

const markChunkReloaded = (name: string) => {
  const reloadKey = getReloadKey(name);
  reloadedChunks.add(reloadKey);

  try {
    window.sessionStorage.setItem(reloadKey, 'true');
  } catch {
    // In-memory fallback above still prevents reload loops when storage is unavailable.
  }
};

const isChunkLoadError = (error: unknown) => {
  if (!(error instanceof Error)) {
    return false;
  }

  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('MIME type') ||
    error.name === 'ChunkLoadError'
  );
};

/**
 * A wrapper for React.lazy that handles ChunkLoadError (e.g., after a new deployment)
 * by forcing a single full page reload to fetch the latest assets.
 */
export function lazyRetry<T extends ComponentType<unknown>>(
  componentImport: () => Promise<{ default: T }>,
  name: string
) {
  return lazy(async () => {
    try {
      return await componentImport();
    } catch (error) {
      console.error(`🚩 Failed to load chunk for ${name}:`, error);
      if (isChunkLoadError(error) && !hasReloadedChunk(name)) {
        markChunkReloaded(name);
        console.warn(`🔄 Auto-refreshing page to load new version of ${name}...`);
        window.location.reload();
      }

      throw error;
    }
  });
}
